import { useState, useEffect } from 'react';
import { X, Download, FileText, ExternalLink, ChevronLeft, ChevronRight, Loader2, Image, AlertCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { LocalNotifications } from '@capacitor/local-notifications';
import useModalBack from '../../hooks/useModalBack';
import { showPermissionDeniedDialog } from '../../services/native';

// PDF.js worker URL from CDN
const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174';

export default function AttachmentViewerModal({
  isOpen,
  onClose,
  src,
  mimeType,
  filename = 'attachment'
}) {
  useModalBack(isOpen, onClose);

  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const isPDF = mimeType === 'application/pdf' || mimeType?.includes('pdf');
  const isImage = mimeType?.startsWith('image/');
  const isNativePlatform = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === 'ios';

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), duration);
  };

  // Reset image states when src changes
  useEffect(() => {
    if (isOpen && isImage) {
      setImgLoading(true);
      setImgError(false);
    }
  }, [isOpen, src, isImage]);

  // Load PDF.js and render PDF pages
  useEffect(() => {
    if (!isOpen || !src || !isPDF) {
      setPdfPages([]);
      setCurrentPage(1);
      setTotalPages(0);
      setPdfError(null);
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);

      try {
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `${PDFJS_CDN}/pdf.min.js`;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
        }

        const loadingTask = window.pdfjsLib.getDocument(src);
        const pdf = await loadingTask.promise;

        if (cancelled) return;

        setTotalPages(pdf.numPages);

        const pages = [];
        const scale = window.devicePixelRatio >= 2 ? 2 : 1.5;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          if (cancelled) return;

          pages.push({
            pageNum: i,
            dataUrl: canvas.toDataURL('image/png'),
            width: viewport.width,
            height: viewport.height
          });
        }

        setPdfPages(pages);
      } catch (err) {
        console.error('PDF loading error:', err);
        if (!cancelled) {
          setPdfError('Failed to load PDF. Try downloading instead.');
        }
      } finally {
        if (!cancelled) {
          setPdfLoading(false);
        }
      }
    };

    loadPdf();

    return () => { cancelled = true; };
  }, [isOpen, src, isPDF]);

  if (!isOpen || !src) return null;

  // Convert blob URL to base64 for native saving
  const blobUrlToBase64 = async (blobUrl) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Handle download
  const handleDownload = async () => {
    if (isNativePlatform) {
      setIsDownloading(true);

      // Check notification permission
      let hasNotificationPermission = false;
      try {
        const permResult = await LocalNotifications.checkPermissions();
        hasNotificationPermission = permResult.display === 'granted';
        if (!hasNotificationPermission) {
          const reqResult = await LocalNotifications.requestPermissions();
          hasNotificationPermission = reqResult.display === 'granted';
          if (!hasNotificationPermission) {
            await showPermissionDeniedDialog('notifications');
          }
        }
      } catch {
        hasNotificationPermission = false;
      }

      showToast(hasNotificationPermission
        ? "Downloading file... We'll notify you when ready."
        : "Downloading file..."
      );

      try {
        const base64Data = await blobUrlToBase64(src);

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const safeFilename = `${timestamp}-${filename}`;

        // On iOS use Documents (accessible via Files app), on Android use Documents
        const result = await Filesystem.writeFile({
          path: safeFilename,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true
        });

        console.log('[AttachmentViewer] File saved:', result.uri);

        if (hasNotificationPermission) {
          const notificationId = Math.floor(Math.random() * 2147483647);
          const extension = filename.split('.').pop()?.toUpperCase() || 'File';

          await LocalNotifications.schedule({
            notifications: [{
              title: `${extension} Downloaded`,
              body: isIOS
                ? `${filename} saved. Check Files app > On My iPhone > Documents.`
                : `${filename} saved to Documents folder.`,
              id: notificationId,
              schedule: { at: new Date(Date.now() + 100) },
              sound: 'default',
              smallIcon: 'ic_notification',
              largeIcon: 'ic_launcher',
              iconColor: '#1A365D'
            }]
          });
        } else {
          showToast(
            isIOS
              ? 'Saved! Check Files app > On My iPhone > Documents.'
              : 'File saved to Documents folder.',
            4000
          );
        }
      } catch (error) {
        console.error('[AttachmentViewer] Download failed:', error);
        showToast('Download failed. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    } else {
      // Web: Use standard download
      const link = document.createElement('a');
      link.href = src;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end md:items-center justify-center z-[9999] md:p-4"
      onClick={handleBackdropClick}
    >
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-auto md:max-w-md z-[10000] animate-fade-in">
          <div className="bg-surface-800 text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 text-sm">
            {isDownloading && (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Bottom sheet on mobile, centered modal on desktop */}
      <div className="relative bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up">
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-0 md:hidden">
          <div className="w-10 h-1 rounded-full bg-surface-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {isPDF ? (
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-red-500" />
              </div>
            ) : (
              <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Image className="w-4 h-4 text-primary-500" />
              </div>
            )}
            <span className="text-sm font-semibold text-surface-900 truncate">
              {filename}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="p-2 text-surface-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors disabled:opacity-50"
              title="Download"
            >
              {isDownloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-surface-500 hover:text-surface-900 hover:bg-surface-100 rounded-xl transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-surface-50 flex items-center justify-center p-4 min-h-[40vh]">
          {isPDF ? (
            <div className="w-full h-full flex flex-col items-center">
              {pdfLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-3" />
                  <p className="text-sm text-surface-500">Loading PDF...</p>
                </div>
              ) : pdfError ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-red-400" />
                  </div>
                  <p className="text-surface-700 font-medium text-center mb-1">{filename}</p>
                  <p className="text-surface-500 text-sm text-center mb-5">{pdfError}</p>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="px-5 py-2.5 text-white rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: 'var(--accent-bg)' }}
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                </div>
              ) : pdfPages.length > 0 ? (
                <div className="w-full space-y-4 pb-4">
                  {totalPages > 1 && (
                    <div className="sticky top-0 z-10 flex items-center justify-center gap-4 py-2 bg-white/90 backdrop-blur-sm rounded-xl shadow-sm">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="text-sm font-medium text-surface-700">
                        {currentPage} / {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  {pdfPages.map((page) => (
                    <div
                      key={page.pageNum}
                      className={`flex justify-center ${totalPages > 1 && page.pageNum !== currentPage ? 'hidden' : ''}`}
                    >
                      <img
                        src={page.dataUrl}
                        alt={`Page ${page.pageNum}`}
                        className="max-w-full rounded-xl shadow-md bg-white"
                        style={{ maxHeight: '70vh' }}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : isImage ? (
            <div className="relative flex items-center justify-center w-full">
              {imgLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-2" />
                  <p className="text-sm text-surface-500">Loading image...</p>
                </div>
              )}
              {imgError ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-surface-400" />
                  </div>
                  <p className="text-surface-700 font-medium text-center mb-1">Failed to load image</p>
                  <p className="text-surface-500 text-sm text-center mb-5">{filename}</p>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="px-5 py-2.5 text-white rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: 'var(--accent-bg)' }}
                  >
                    <Download className="w-4 h-4" />
                    Download Image
                  </button>
                </div>
              ) : (
                <img
                  src={src}
                  alt={filename}
                  className={`max-w-full max-h-[70vh] object-contain rounded-xl shadow-md transition-opacity ${imgLoading ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={() => setImgLoading(false)}
                  onError={() => { setImgLoading(false); setImgError(true); }}
                />
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-surface-400" />
              </div>
              <p className="text-surface-600 mb-4">Preview not available</p>
              <button
                type="button"
                onClick={handleDownload}
                className="px-5 py-2.5 text-white rounded-xl text-sm font-medium flex items-center gap-2 mx-auto transition-colors"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                <Download className="w-4 h-4" />
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
